import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import SeverityBadge from "@/components/SeverityBadge";
import { toast } from "sonner";
import { ArrowLeft, Zap, Send, CheckCircle2, ShieldCheck, Terminal, ChevronRight } from "lucide-react";

const STATUS_OPTIONS = ["open", "investigating", "contained", "resolved"];

export default function IncidentDetail() {
  const { id } = useParams();
  const [inc, setInc] = useState(null);
  const [logs, setLogs] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [note, setNote] = useState("");

  const load = async () => {
    const [i, l] = await Promise.all([
      api.get(`/incidents/${id}`),
      api.get(`/incidents/${id}/logs`),
    ]);
    setInc(i.data);
    setLogs(l.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const runAI = async () => {
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/ai/analyze/${id}`);
      setInc((p) => ({ ...p, ai_analysis: data }));
      toast.success("Analysis complete");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const changeStatus = async (status) => {
    const { data } = await api.patch(`/incidents/${id}`, { status });
    setInc(data);
    toast.success(`Status → ${status}`);
  };

  const addNote = async () => {
    if (!note.trim()) return;
    const { data } = await api.post(`/incidents/${id}/notes`, { content: note });
    setInc(data);
    setNote("");
    toast.success("Note added");
  };

  if (!inc) return <div className="p-8 text-[#8E9196] font-mono text-sm">Loading incident…</div>;

  const ai = inc.ai_analysis;

  return (
    <div className="p-8 max-w-[1400px] mx-auto">
      <Link to="/incidents" data-testid="back-to-incidents" className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-[#8E9196] hover:text-white mb-4">
        <ArrowLeft className="w-3 h-3" /> Back to incidents
      </Link>

      <div className="flex items-start justify-between gap-6 mb-6">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <SeverityBadge severity={inc.severity} />
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#8E9196]">#{inc.id.slice(0, 8)}</span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#00F0FF]">{inc.status}</span>
          </div>
          <h1 className="font-display font-black text-3xl md:text-4xl leading-tight">{inc.title}</h1>
          <p className="text-[#8E9196] mt-2 max-w-3xl">{inc.description}</p>
          <div className="mt-3 flex gap-4 text-xs font-mono text-[#8E9196] uppercase tracking-widest">
            <span>Asset: <span className="text-white">{inc.asset || "-"}</span></span>
            <span>Assignee: <span className="text-white">{inc.assignee || "unassigned"}</span></span>
            <span>Opened: <span className="text-white">{new Date(inc.created_at).toLocaleString()}</span></span>
          </div>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <button
            onClick={runAI}
            disabled={analyzing}
            data-testid="ai-analyze-btn"
            className="tracing-beam inline-flex items-center justify-center gap-2 border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black px-4 py-2 rounded-sm font-mono uppercase text-xs tracking-widest disabled:opacity-50"
          >
            <Zap className="w-3.5 h-3.5" /> {analyzing ? "Reasoning…" : "AI Root-Cause Analysis"}
          </button>
          <div className="flex items-center border border-white/15 rounded-sm overflow-hidden">
            {STATUS_OPTIONS.map((s) => (
              <button key={s} onClick={() => changeStatus(s)}
                data-testid={`status-${s}`}
                className={`px-2.5 py-1.5 font-mono uppercase text-[10px] tracking-widest transition-colors ${
                  inc.status === s ? "bg-white/10 text-white" : "text-[#8E9196] hover:text-white"
                }`}>{s}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: Related Logs */}
        <div className="border border-white/10 bg-[#0a0a0a] rounded-sm scan-lines relative" data-testid="incident-logs">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-[#121212] text-[11px] font-mono uppercase tracking-widest text-[#8E9196]">
            <Terminal className="w-3.5 h-3.5" /> correlated logs ({logs.length})
          </div>
          <div className="max-h-[520px] overflow-y-auto p-3 font-mono text-[11px] space-y-1.5">
            {logs.length === 0 && <div className="text-[#52525B] p-2">No correlated logs.</div>}
            {logs.map((l) => (
              <div key={l.id} className="border-b border-white/[0.04] pb-1.5">
                <div className="flex items-center gap-2">
                  <SeverityBadge severity={l.severity} />
                  <span className="text-[#00F0FF]">{l.host}</span>
                  <span className="text-[#52525B]">{new Date(l.timestamp).toISOString().slice(11, 19)}</span>
                </div>
                <div className="text-white mt-1 break-words">{l.message}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Center: AI Analysis */}
        <div className="border border-white/10 bg-[#121212] rounded-sm p-5" data-testid="ai-panel">
          <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196] flex items-center gap-2">
            <Zap className="w-3.5 h-3.5 text-[#00F0FF]" /> Sentinel AI
          </div>
          <div className="font-display text-2xl mt-1">Reasoning chain</div>

          {!ai && (
            <div className="mt-6 text-center text-sm text-[#8E9196] py-10">
              No analysis yet.<br />
              <span className="text-xs">Click &ldquo;AI Root-Cause Analysis&rdquo; to generate.</span>
            </div>
          )}

          {ai && (
            <div className="mt-4 space-y-4">
              <div className="border border-[#00F0FF]/40 bg-[#00F0FF]/[0.04] rounded-sm p-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#00F0FF]">Verdict · confidence {ai.confidence}%</div>
                <div className="text-white mt-1 font-medium">{ai.verdict}</div>
              </div>

              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mb-1">Root cause</div>
                <p className="text-sm">{ai.root_cause}</p>
              </div>

              {ai.attack_stage && (
                <div className="flex gap-2 flex-wrap">
                  <span className="border border-white/15 rounded-sm px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-[#8E9196]">
                    stage: <span className="text-white">{ai.attack_stage}</span>
                  </span>
                  {(ai.mitre_techniques || []).map((t) => (
                    <span key={t} className="border border-white/15 rounded-sm px-2 py-0.5 font-mono text-[10px] text-[#FF9F0A]">{t}</span>
                  ))}
                </div>
              )}

              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mb-2">Reasoning steps</div>
                <ol className="space-y-2">
                  {(ai.reasoning || []).map((r, i) => (
                    <li key={i} className="flex gap-2 font-mono text-xs">
                      <span className="text-[#00F0FF] shrink-0">[{String(i + 1).padStart(2, "0")}]</span>
                      <span>{r}</span>
                    </li>
                  ))}
                </ol>
              </div>

              {ai.iocs?.length ? (
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mb-1">Extracted IOCs</div>
                  <div className="flex flex-wrap gap-1">
                    {ai.iocs.map((i) => <span key={i} className="font-mono text-[11px] border border-white/15 rounded-sm px-1.5 py-0.5">{i}</span>)}
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>

        {/* Right: Remediation + Notes */}
        <div className="space-y-4">
          <div className="border border-white/10 bg-[#121212] rounded-sm p-5" data-testid="remediation-panel">
            <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196] flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5" /> Remediation
            </div>
            <div className="font-display text-lg mt-1 mb-3">Recommended actions</div>
            {!ai?.remediation?.length && <div className="text-sm text-[#8E9196]">Run AI analysis to generate steps.</div>}
            <ul className="space-y-3">
              {(ai?.remediation || []).map((r, i) => (
                <li key={i} className="flex gap-3">
                  <ChevronRight className="w-4 h-4 text-[#00F0FF] shrink-0 mt-0.5" />
                  <div>
                    <div className="text-xs font-mono uppercase tracking-widest" style={{ color: r.priority === "immediate" ? "#FF3B30" : r.priority === "short_term" ? "#FF9F0A" : "#34C759" }}>
                      {r.priority?.replace("_", " ")}
                    </div>
                    <div className="text-sm">{r.action}</div>
                    <div className="text-xs text-[#8E9196] mt-0.5">{r.detail}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="border border-white/10 bg-[#121212] rounded-sm p-5" data-testid="collab-panel">
            <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196]">Collaboration</div>
            <div className="font-display text-lg mt-1 mb-3">Notes ({inc.notes?.length || 0})</div>

            <div className="space-y-2 max-h-40 overflow-y-auto mb-3">
              {(inc.notes || []).map((n) => (
                <div key={n.id} className="border-l-2 border-[#00F0FF]/40 pl-3 py-1">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">
                    {n.author_name} · {new Date(n.created_at).toLocaleString()}
                  </div>
                  <div className="text-sm">{n.content}</div>
                </div>
              ))}
              {!inc.notes?.length && <div className="text-sm text-[#52525B]">No notes yet.</div>}
            </div>

            <div className="flex gap-2">
              <input
                data-testid="note-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add a note…"
                className="flex-1 bg-transparent border border-white/15 px-3 py-2 rounded-sm font-mono text-xs outline-none focus:border-[#00F0FF]"
              />
              <button data-testid="submit-note" onClick={addNote}
                className="border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black px-3 py-2 rounded-sm">
                <Send className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
